import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import {
  api,
  ApiError,
  type AdminSpace,
  type AdminSpaceDetail,
  type AdminUser,
  type CreatedUser,
  type ImportedMessage,
  type IngestionReport,
  type MailSettings,
  type Me,
  type TestMailResult,
} from '../../lib/api'
import { layout } from '../../design/tokens'

type Onglet = 'espaces' | 'utilisateurs' | 'courrier' | 'compte'

/**
 * Console d'administration — hors du produit maquetté.
 *
 * Aucune planche du cahier ne la décrit : c'est un outil d'exploitation, pas un écran destiné
 * au couple. Elle reprend néanmoins les jetons du design system pour rester cohérente, et
 * s'affiche sur toute la largeur disponible — les tableaux ne tiennent pas dans la colonne
 * mobile de 390 px.
 */
export function AdminScreen({ me }: { me: Me }) {
  const [onglet, setOnglet] = useState<Onglet>('espaces')

  if (!me.isAdmin) {
    return (
      <Cadre>
        <h1 className="font-serif text-screen">Administration</h1>
        <p className="mt-4 text-body text-ink-muted">
          Ce compte n'a pas les droits d'administration.
        </p>
      </Cadre>
    )
  }

  return (
    <Cadre>
      <header className="flex flex-wrap items-baseline justify-between gap-3" style={{ paddingBlock: 16 }}>
        <h1 className="font-serif text-screen">Administration</h1>
        <div className="flex flex-wrap items-center gap-4">
          <p className="text-meta text-ink-muted">Connecté comme {me.displayName}</p>
          <Link
            to="/"
            className="flex items-center rounded-button bg-accent px-4 text-card-title font-semibold text-card"
            style={{ height: 48 }}
          >
            Quitter l'administration
          </Link>
        </div>
      </header>

      <nav className="flex gap-2" style={{ paddingBottom: 16 }}>
        {(['espaces', 'utilisateurs', 'courrier', 'compte'] as const).map((cible) => (
          <button
            key={cible}
            type="button"
            onClick={() => setOnglet(cible)}
            aria-pressed={onglet === cible}
            className="rounded-full px-4 text-meta font-semibold capitalize"
            style={{
              minHeight: 36,
              background: onglet === cible ? 'var(--color-accent)' : 'var(--color-card)',
              color: onglet === cible ? 'var(--color-card)' : 'var(--color-ink)',
              border: `1px solid ${onglet === cible ? 'var(--color-accent)' : 'var(--color-border)'}`,
            }}
          >
            {cible}
          </button>
        ))}
      </nav>

      {onglet === 'espaces' ? <Espaces /> : null}
      {onglet === 'utilisateurs' ? <Utilisateurs moi={me} /> : null}
      {onglet === 'courrier' ? <Courrier /> : null}
      {onglet === 'compte' ? <Compte /> : null}
    </Cadre>
  )
}

/**
 * Boîte mail de l'application — étape A de la décision M1.
 *
 * Un compte Gmail dédié, un mot de passe d'application, et l'application sait enfin envoyer :
 * liens de connexion et récupération de mot de passe, en souffrance depuis le premier jour.
 * La lecture des messages entrants viendra à l'étape B.
 */
function Courrier() {
  const queryClient = useQueryClient()
  const { data, isPending } = useQuery<MailSettings>({
    queryKey: ['admin', 'courrier'],
    queryFn: api.adminMail,
  })

  const [adresse, setAdresse] = useState<string | null>(null)
  const [nom, setNom] = useState<string | null>(null)
  const [motDePasse, setMotDePasse] = useState('')
  const [afficher, setAfficher] = useState(false)
  const [actif, setActif] = useState<boolean | null>(null)
  const [lecture, setLecture] = useState<boolean | null>(null)
  const [rythme, setRythme] = useState<string | null>(null)
  const [destinataire, setDestinataire] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  // Les champs suivent les données tant que rien n'a été saisi : `null` signifie « pas encore
  // touché ». Sans cette distinction, la réponse du serveur écraserait une saisie en cours.
  const valeurAdresse = adresse ?? data?.address ?? ''
  const valeurNom = nom ?? data?.senderName ?? ''
  const valeurActif = actif ?? data?.sendEnabled ?? false
  const valeurLecture = lecture ?? data?.receiveEnabled ?? false
  const valeurRythme = rythme ?? String(data?.pollMinutes ?? 2)

  const enregistrer = useMutation({
    mutationFn: () =>
      // Propriétés greffées seulement si elles portent une valeur : `exactOptionalPropertyTypes`
      // distingue « absent » de « présent et indéfini », et le serveur lit cette différence —
      // un mot de passe absent laisse le précédent en place.
      api.adminSaveMail({
        address: valeurAdresse.trim(),
        ...(valeurNom.trim() ? { senderName: valeurNom.trim() } : {}),
        ...(motDePasse.trim() ? { appPassword: motDePasse.trim() } : {}),
        sendEnabled: valeurActif,
        receiveEnabled: valeurLecture,
        pollMinutes: Number(valeurRythme) || 2,
      }),
    onSuccess: async () => {
      setMotDePasse('')
      setMessage('Réglages enregistrés.')
      setErreur(null)
      await queryClient.invalidateQueries({ queryKey: ['admin', 'courrier'] })
    },
    onError: (cause) =>
      setErreur(cause instanceof ApiError ? cause.message : "L'enregistrement a échoué."),
  })

  const tester = useMutation({
    mutationFn: () => api.adminTestMail(destinataire.trim()),
    onSuccess: (resultat: TestMailResult) => {
      setMessage(resultat.sent ? resultat.message : null)
      // L'échec renvoie le refus de Gmail mot pour mot : « authentication failed » se corrige,
      // « une erreur est survenue » ne se corrige pas.
      setErreur(resultat.sent ? null : resultat.message)
    },
    onError: (cause) =>
      setErreur(cause instanceof ApiError ? cause.message : "Le test a échoué."),
  })

  if (isPending) {
    return <Chargement />
  }

  return (
    <section className="rounded-card border border-border bg-card p-4" style={{ maxWidth: 560 }}>
      <h2 className="font-serif text-record">Boîte mail</h2>
      <p className="mt-2 text-meta text-ink-muted">
        Un compte Gmail dédié à l'application. Le mot de passe d'application se crée dans les
        réglages de sécurité du compte Google, et exige d'y avoir activé la validation en deux
        étapes.
      </p>

      <label className="mt-4 block text-label font-semibold uppercase text-ink-muted" htmlFor="adresse-mail">
        Adresse
      </label>
      <input
        id="adresse-mail"
        type="email"
        autoComplete="off"
        value={valeurAdresse}
        onChange={(event) => {
          setAdresse(event.target.value)
          setMessage(null)
          setErreur(null)
        }}
        placeholder="organisateur.famille@gmail.com"
        className="mt-2 w-full rounded-field border-[1.5px] border-border bg-card px-4 outline-none focus:border-accent"
        style={{ height: 56, fontSize: 17 }}
      />

      <label className="mt-4 block text-label font-semibold uppercase text-ink-muted" htmlFor="nom-mail">
        Nom affiché
      </label>
      <input
        id="nom-mail"
        value={valeurNom}
        onChange={(event) => setNom(event.target.value)}
        placeholder="Organisateur"
        className="mt-2 w-full rounded-field border-[1.5px] border-border bg-card px-4 outline-none focus:border-accent"
        style={{ height: 56, fontSize: 17 }}
      />

      <label className="mt-4 block text-label font-semibold uppercase text-ink-muted" htmlFor="mdp-mail">
        Mot de passe d'application
      </label>
      <input
        id="mdp-mail"
        type={afficher ? 'text' : 'password'}
        autoComplete="new-password"
        value={motDePasse}
        onChange={(event) => {
          setMotDePasse(event.target.value)
          setMessage(null)
          setErreur(null)
        }}
        placeholder={data?.hasPassword ? 'Inchangé — saisir pour remplacer' : 'abcd efgh ijkl mnop'}
        className="mt-2 w-full rounded-field border-[1.5px] border-border bg-card px-4 outline-none focus:border-accent"
        style={{ height: 56, fontSize: 17 }}
      />
      <button
        type="button"
        onClick={() => setAfficher((valeur) => !valeur)}
        className="mt-2 text-meta font-semibold text-accent"
      >
        {afficher ? 'Masquer' : 'Afficher'}
      </button>
      <p className="mt-2 text-meta text-ink-muted">
        Les espaces affichés par Google sont retirés automatiquement. Le mot de passe est chiffré
        avant d'être enregistré et n'est jamais réaffiché.
      </p>

      <label className="mt-4 flex items-center gap-3">
        <input
          type="checkbox"
          checked={valeurActif}
          onChange={(event) => setActif(event.target.checked)}
          style={{ width: 20, height: 20 }}
        />
        <span className="text-body">Envoyer les messages par cette boîte</span>
      </label>

      {/* Interrupteur distinct de l'envoi : on peut vouloir envoyer sans relever, le temps
          d'attribuer les alias aux espaces. */}
      <label className="mt-3 flex items-center gap-3">
        <input
          type="checkbox"
          checked={valeurLecture}
          onChange={(event) => setLecture(event.target.checked)}
          style={{ width: 20, height: 20 }}
        />
        <span className="text-body">Relever la boîte et créer les fiches</span>
      </label>

      <label
        className="mt-4 block text-label font-semibold uppercase text-ink-muted"
        htmlFor="rythme-mail"
      >
        Relever toutes les
      </label>
      <div className="mt-2 flex items-center gap-3">
        <input
          id="rythme-mail"
          type="number"
          min={1}
          max={60}
          value={valeurRythme}
          onChange={(event) => {
            setRythme(event.target.value)
            setMessage(null)
            setErreur(null)
          }}
          className="rounded-field border-[1.5px] border-border bg-card px-4 outline-none focus:border-accent"
          style={{ height: 56, fontSize: 17, width: 110 }}
        />
        <span className="text-body">minutes</span>
      </div>
      <p className="mt-2 text-meta text-ink-muted">
        De 1 à 60. Le changement prend effet à la relève suivante, sans redémarrage.
      </p>

      <button
        type="button"
        disabled={valeurAdresse.trim().length === 0 || enregistrer.isPending}
        onClick={() => enregistrer.mutate()}
        className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
        style={{ height: layout.primaryButtonHeight }}
      >
        {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
      </button>

      <hr className="mt-6 border-border" />

      <h3 className="mt-4 text-card-title font-semibold">Tester l'envoi</h3>
      <p className="mt-2 text-meta text-ink-muted">
        Un message part vers l'adresse indiquée. C'est la seule façon de vérifier que Gmail
        accepte le mot de passe.
      </p>
      <input
        id="test-mail"
        type="email"
        value={destinataire}
        onChange={(event) => setDestinataire(event.target.value)}
        placeholder="votre.adresse@exemple.fr"
        className="mt-2 w-full rounded-field border-[1.5px] border-border bg-card px-4 outline-none focus:border-accent"
        style={{ height: 56, fontSize: 17 }}
      />
      <button
        type="button"
        disabled={destinataire.trim().length === 0 || tester.isPending}
        onClick={() => tester.mutate()}
        className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold disabled:opacity-60"
        style={{ height: layout.primaryButtonHeight }}
      >
        {tester.isPending ? 'Envoi…' : 'Envoyer un message de test'}
      </button>

      {message ? <p className="mt-4 text-meta text-accent">{message}</p> : null}
      {erreur ? <p className="mt-4 text-meta text-danger">{erreur}</p> : null}
      {erreur && estUnRefusDIdentifiants(erreur) ? <AideIdentifiants /> : null}

      {data?.updatedAt ? (
        <p className="mt-4 text-meta text-ink-muted">
          Dernière modification le {new Date(data.updatedAt).toLocaleString('fr-FR')}.
        </p>
      ) : null}

      <hr className="mt-6 border-border" />
      <Releve />
    </section>
  )
}

/**
 * Relève manuelle et journal des messages.
 *
 * Le journal porte aussi les refus et leur motif : c'est lui qui répond à « pourquoi mon
 * message n'est pas arrivé ? », sans avoir à fouiller les journaux du serveur.
 */
function Releve() {
  const queryClient = useQueryClient()
  const [compte, setCompte] = useState<string | null>(null)

  const { data } = useQuery<readonly ImportedMessage[]>({
    queryKey: ['admin', 'courrier', 'messages'],
    queryFn: api.adminMailMessages,
  })

  const { data: espaces } = useQuery<readonly AdminSpace[]>({
    queryKey: ['admin', 'espaces'],
    queryFn: api.adminSpaces,
  })

  const relever = useMutation({
    mutationFn: api.adminRelever,
    onSuccess: async (rapport: IngestionReport) => {
      setCompte(
        rapport.error ??
          `${rapport.read} message(s) lu(s), ${rapport.imported} importé(s), ${rapport.rejected} écarté(s).`,
      )
      await queryClient.invalidateQueries({ queryKey: ['admin', 'courrier', 'messages'] })
    },
    onError: () => setCompte('La relève a échoué.'),
  })

  return (
    <>
      <h3 className="mt-4 text-card-title font-semibold">Messages reçus</h3>
      <p className="mt-2 text-meta text-ink-muted">
        La boîte est relevée toutes les deux minutes. Rien n'y est jamais supprimé ni marqué
        comme lu.
      </p>

      <button
        type="button"
        disabled={relever.isPending}
        onClick={() => relever.mutate()}
        className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold disabled:opacity-60"
        style={{ height: layout.primaryButtonHeight }}
      >
        {relever.isPending ? 'Relève…' : 'Relever maintenant'}
      </button>

      {compte ? <p className="mt-3 text-meta text-ink-muted">{compte}</p> : null}

      {data && data.length > 0 ? (
        <ul className="mt-4 flex flex-col gap-2">
          {data.map((message) => (
            <LigneMessage key={message.id} message={message} espaces={espaces ?? []} />
          ))}
        </ul>
      ) : (
        <p className="mt-4 text-meta text-ink-muted">Aucun message relevé pour l'instant.</p>
      )}
    </>
  )
}

/**
 * Gmail renvoie « 535 5.7.8 Username and Password not accepted » sans dire laquelle des cinq
 * causes habituelles s'applique. On reconnaît ce refus précis pour afficher la liste, plutôt que
 * de laisser l'administrateur avec un code numérique et un lien.
 */
function estUnRefusDIdentifiants(message: string) {
  return message.includes('5.7.8') || message.includes('BadCredentials')
}

function AideIdentifiants() {
  return (
    <div className="mt-3 rounded-card bg-surface p-4">
      <p className="text-meta font-semibold">Google refuse le couple adresse + mot de passe.</p>
      <ul className="mt-2 flex flex-col gap-2 text-meta text-ink-muted">
        <li>
          · Vérifiez qu'il s'agit d'un <b>mot de passe d'application</b> et non du mot de passe du
          compte : Gmail refuse ce dernier en SMTP. Celui d'application fait seize lettres
          minuscules.
        </li>
        <li>· La validation en deux étapes doit être active sur ce compte Google.</li>
        <li>
          · Le mot de passe doit avoir été créé <b>sur ce compte-là</b>. Créé pendant qu'un autre
          compte Google était ouvert, il appartient à celui-là.
        </li>
        <li>
          · Changer le mot de passe d'un compte Google <b>révoque tous</b> ses mots de passe
          d'application.
        </li>
        <li>
          · Sur un compte tout neuf, l'accès SMTP peut rester bloqué tant qu'il n'a pas servi
          normalement : connectez-vous une fois à Gmail, puis réessayez.
        </li>
      </ul>
      <p className="mt-3 text-meta text-ink-muted">
        Le plus rapide est de révoquer le mot de passe existant et d'en créer un neuf.
      </p>
    </div>
  )
}

/**
 * Une ligne du journal, et ce qu'on peut en faire.
 *
 * Un message écarté n'est pas une impasse : on le range dans l'espace de son choix, on le classe
 * sans suite, ou on le retire de la boîte. Sans ces actions, l'écran ne servait qu'à constater —
 * et un message adressé à un alias inexistant restait perdu.
 */
function LigneMessage({
  message,
  espaces,
}: {
  message: ImportedMessage
  espaces: readonly AdminSpace[]
}) {
  const queryClient = useQueryClient()
  const [cible, setCible] = useState('')
  const [retour, setRetour] = useState<string | null>(null)

  const rafraichir = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'courrier', 'messages'] })

  const ranger = useMutation({
    mutationFn: () => api.adminRangerMessage(message.id, cible),
    onSuccess: async () => {
      setRetour('Fiche créée, à classer dans l\'espace.')
      await rafraichir()
    },
    onError: (cause: unknown) =>
      setRetour(cause instanceof ApiError ? cause.message : 'Le rangement a échoué.'),
  })

  const ignorer = useMutation({
    mutationFn: () => api.adminIgnorerMessage(message.id),
    onSuccess: rafraichir,
  })

  const supprimer = useMutation({
    mutationFn: () => api.adminSupprimerLeMail(message.id),
    onSuccess: async (resultat: TestMailResult) => {
      setRetour(resultat.message)
      await rafraichir()
    },
    onError: () => setRetour('La suppression a échoué.'),
  })

  const traite = message.itemId !== null

  return (
    <li className="rounded-card bg-surface p-3">
      <p className="text-meta font-semibold">{message.subject || '(sans objet)'}</p>
      <p className="mt-1 text-meta text-ink-muted">
        {message.sender ?? 'expéditeur inconnu'}
        {message.alias ? ` · +${message.alias}` : ''}
        {message.spaceName ? ` · ${message.spaceName}` : ''}
      </p>
      <p
        className="mt-1 text-meta"
        style={{ color: message.rejectedReason ? 'var(--color-danger)' : 'var(--color-accent)' }}
      >
        {message.rejectedReason ?? 'Fiche créée.'}
      </p>

      {message.bodyText ? (
        <p className="mt-2 line-clamp-3 text-meta text-ink-muted" style={{ whiteSpace: 'pre-wrap' }}>
          {message.bodyText}
        </p>
      ) : null}

      {!traite ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            aria-label="Espace de destination"
            value={cible}
            onChange={(event) => {
              setCible(event.target.value)
              setRetour(null)
            }}
            className="rounded-field border border-border bg-card px-3 text-meta outline-none"
            style={{ height: 40 }}
          >
            <option value="">Ranger dans…</option>
            {espaces.map((espace) => (
              <option key={espace.id} value={espace.id}>
                {espace.name}
              </option>
            ))}
          </select>

          <button
            type="button"
            disabled={cible === '' || ranger.isPending}
            onClick={() => ranger.mutate()}
            className="rounded-button bg-accent px-3 text-meta font-semibold text-card disabled:opacity-40"
            style={{ height: 40 }}
          >
            Ranger
          </button>

          <button
            type="button"
            onClick={() => ignorer.mutate()}
            className="rounded-button border border-border bg-card px-3 text-meta font-semibold"
            style={{ height: 40 }}
          >
            Classer sans suite
          </button>

          {!message.mailDeleted ? (
            <button
              type="button"
              disabled={supprimer.isPending}
              onClick={() => supprimer.mutate()}
              className="rounded-button border px-3 text-meta font-semibold disabled:opacity-40"
              style={{ height: 40, borderColor: 'var(--color-danger)', color: 'var(--color-danger)' }}
            >
              {supprimer.isPending ? 'Suppression…' : 'Supprimer le mail'}
            </button>
          ) : (
            <span className="text-meta text-ink-muted">Mail mis à la corbeille.</span>
          )}
        </div>
      ) : null}

      {retour ? <p className="mt-2 text-meta text-ink-muted">{retour}</p> : null}
    </li>
  )
}

function Espaces() {
  const [ouvert, setOuvert] = useState<string | null>(null)

  const { data, isPending } = useQuery<readonly AdminSpace[]>({
    queryKey: ['admin', 'espaces'],
    queryFn: api.adminSpaces,
  })

  const { data: utilisateurs } = useQuery<readonly AdminUser[]>({
    queryKey: ['admin', 'utilisateurs'],
    queryFn: api.adminUsers,
  })

  if (isPending) {
    return <Chargement />
  }

  return (
    <>
    <CreerEspace utilisateurs={utilisateurs ?? []} />

    <ul className="flex flex-col gap-3">
      {data?.map((espace) => (
        <li key={espace.id} className="rounded-card border border-border bg-card p-4">
          <div className="flex flex-wrap items-center gap-3">
            <span
              className="flex items-center justify-center rounded-field font-semibold text-card"
              style={{
                width: 40,
                height: 40,
                fontSize: 15,
                background: `var(--color-cat-${espace.emblemHue}-mark)`,
              }}
            >
              {espace.emblemInitials}
            </span>
            <div className="grow">
              <p className="text-card-title font-semibold">{espace.name}</p>
              <p className="mt-1 text-meta text-ink-muted">
                {/* Le propriétaire en tête : dans une liste d'espaces qu'on n'a pas créés, c'est
                    la première chose qu'on cherche — à qui parler de celui-ci. */}
                {espace.ownerName ? `${espace.ownerName} · ` : ''}
                {espace.memberCount} membre{espace.memberCount > 1 ? 's' : ''} · {espace.itemCount}{' '}
                fiche{espace.itemCount > 1 ? 's' : ''} · créé le{' '}
                {new Date(espace.createdAt).toLocaleDateString('fr-FR')}
              </p>
            </div>
            {/* Ouvrir l'espace comme on l'ouvrirait chez soi, sans s'y ajouter : s'ajouter
                laisse une trace permanente, fait entrer dans les soldes et les partages, et
                oblige à penser à ressortir. L'écran de l'espace dira qu'on y est en
                administrateur. */}
            <Link
              to={`/espace/${espace.id}`}
              className="shrink-0 text-meta font-semibold text-accent"
            >
              Ouvrir
            </Link>
            <button
              type="button"
              onClick={() => setOuvert(ouvert === espace.id ? null : espace.id)}
              className="shrink-0 text-meta font-semibold text-accent"
            >
              {ouvert === espace.id ? 'Fermer' : 'Détail'}
            </button>
          </div>

          {ouvert === espace.id ? <DetailEspace spaceId={espace.id} /> : null}
        </li>
      ))}

      {data?.length === 0 ? <p className="text-body text-ink-muted">Aucun espace.</p> : null}
    </ul>
    </>
  )
}

/** Crée un espace au nom d'un utilisateur, qui en devient propriétaire. */
function CreerEspace({ utilisateurs }: { utilisateurs: readonly AdminUser[] }) {
  const [nom, setNom] = useState('')
  const [proprietaire, setProprietaire] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const creer = useMutation({
    mutationFn: () => api.adminCreateSpace(nom.trim(), proprietaire),
    onSuccess: async () => {
      setNom('')
      setMessage('Espace créé, avec ses six catégories.')
      await queryClient.invalidateQueries({ queryKey: ['admin'] })
    },
    onError: (cause: unknown) =>
      setMessage(cause instanceof ApiError ? cause.message : 'La création a échoué.'),
  })

  return (
    <section className="mb-6 rounded-card border border-border bg-card p-4">
      <h2 className="font-serif text-record">Créer un espace</h2>

      <div className="mt-3 flex flex-wrap gap-3">
        <input
          value={nom}
          onChange={(event) => {
            setNom(event.target.value)
            setMessage(null)
          }}
          placeholder="Nom de l'espace"
          className="grow rounded-field border border-border bg-card px-4 outline-none"
          style={{ height: 48, fontSize: 16, minWidth: 180 }}
        />
        <select
          value={proprietaire}
          onChange={(event) => setProprietaire(event.target.value)}
          className="grow rounded-field border border-border bg-card px-4 outline-none"
          style={{ height: 48, fontSize: 16, minWidth: 180 }}
        >
          <option value="">Propriétaire…</option>
          {utilisateurs.map((utilisateur) => (
            <option key={utilisateur.id} value={utilisateur.id}>
              {utilisateur.displayName} · {utilisateur.email}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={nom.trim().length === 0 || proprietaire === '' || creer.isPending}
          onClick={() => creer.mutate()}
          className="rounded-button bg-accent px-4 text-card-title font-semibold text-card disabled:opacity-40"
          style={{ height: 48 }}
        >
          Créer
        </button>
      </div>

      {message ? <p className="mt-2 text-meta text-ink-muted">{message}</p> : null}
    </section>
  )
}

function DetailEspace({ spaceId }: { spaceId: string }) {
  const queryClient = useQueryClient()
  const [confirmation, setConfirmation] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)

  const { data } = useQuery<AdminSpaceDetail>({
    queryKey: ['admin', 'espace', spaceId],
    queryFn: () => api.adminSpace(spaceId),
  })

  // L'adresse de la boîte, pour composer l'adresse de dépôt complète sous l'alias. Déjà en
  // cache dès que l'onglet Courrier a été ouvert.
  const { data: courrier } = useQuery<MailSettings>({
    queryKey: ['admin', 'courrier'],
    queryFn: api.adminMail,
  })

  const rafraichir = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin'] })
  }

  const retirer = useMutation({
    mutationFn: (userId: string) => api.adminRemoveMember(spaceId, userId),
    onSuccess: rafraichir,
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : 'Le retrait a échoué.'),
  })

  const supprimer = useMutation({
    mutationFn: () => api.adminDeleteSpace(spaceId, confirmation),
    onSuccess: rafraichir,
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : 'La suppression a échoué.'),
  })

  return (
    <section className="mt-4 rounded-card bg-surface p-4">
      <p className="text-label font-semibold uppercase text-ink-muted">Nom et adresse de dépôt</p>
      <Renommer
        spaceId={spaceId}
        nomActuel={data?.space.name ?? ''}
        aliasActuel={data?.space.emailAlias ?? null}
        adresseBoite={courrier?.address ?? null}
      />

      <p className="mt-6 text-label font-semibold uppercase text-ink-muted">Membres</p>
      <ul className="mt-3 flex flex-col gap-2">
        {data?.members.map((membre) => (
          <li key={membre.userId} className="flex flex-wrap items-center gap-3">
            <span className="grow text-body">
              {membre.displayName} <span className="text-ink-muted">· {membre.email}</span>{' '}
              <span className="text-meta text-ink-muted">({membre.role})</span>
            </span>
            <button
              type="button"
              onClick={() => retirer.mutate(membre.userId)}
              className="text-meta font-semibold"
              style={{ color: 'var(--color-danger)' }}
            >
              Retirer
            </button>
          </li>
        ))}
      </ul>

      <AjouterMembre spaceId={spaceId} membres={data?.members ?? []} />

      <p className="mt-6 text-label font-semibold uppercase text-ink-muted">Suppression définitive</p>
      <p className="mt-2 text-meta text-ink-muted">
        Efface l'espace et tout ce qu'il contient. Retapez « {data?.space.name} » pour confirmer.
      </p>
      <div className="mt-3 flex flex-wrap gap-3">
        <input
          value={confirmation}
          onChange={(event) => {
            setConfirmation(event.target.value)
            setErreur(null)
          }}
          placeholder={data?.space.name}
          className="grow rounded-field border border-border bg-card px-4 outline-none"
          style={{ height: 48, fontSize: 16, minWidth: 200 }}
        />
        <button
          type="button"
          disabled={confirmation.trim() !== data?.space.name}
          onClick={() => supprimer.mutate()}
          className="rounded-button px-4 text-card-title font-semibold text-card disabled:opacity-40"
          style={{ height: 48, background: 'var(--color-danger)' }}
        >
          Supprimer
        </button>
      </div>

      {erreur ? (
        <p className="mt-3 text-meta" style={{ color: 'var(--color-danger)' }}>
          {erreur}
        </p>
      ) : null}
    </section>
  )
}

/** Renommer un espace. L'emblème suit le nom, sinon « Voyages » garderait la marque « Fa ». */
function Renommer({
  spaceId,
  nomActuel,
  aliasActuel,
  adresseBoite,
}: {
  spaceId: string
  nomActuel: string
  aliasActuel: string | null
  adresseBoite: string | null
}) {
  const [nom, setNom] = useState(nomActuel)
  const [alias, setAlias] = useState(aliasActuel ?? '')
  const [message, setMessage] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const enregistrer = useMutation({
    mutationFn: () => api.adminRenameSpace(spaceId, nom.trim(), alias.trim().toLowerCase()),
    onSuccess: async () => {
      setMessage('Enregistré.')
      await queryClient.invalidateQueries({ queryKey: ['admin'] })
    },
    onError: (cause: unknown) =>
      setMessage(cause instanceof ApiError ? cause.message : "L'enregistrement a échoué."),
  })

  // L'adresse complète, montrée telle qu'on la tapera dans son client de messagerie. Sans elle,
  // il faudrait deviner comment l'alias se combine à l'adresse de la boîte.
  const adresseComplete =
    adresseBoite && alias.trim()
      ? adresseBoite.replace('@', `+${alias.trim().toLowerCase()}@`)
      : null

  const modifie = nom.trim() !== nomActuel || alias.trim().toLowerCase() !== (aliasActuel ?? '')

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-3">
        <input
          aria-label="Nom de l'espace"
          value={nom}
          onChange={(event) => {
            setNom(event.target.value)
            setMessage(null)
          }}
          className="grow rounded-field border border-border bg-card px-4 outline-none"
          style={{ height: 48, fontSize: 16, minWidth: 200 }}
        />
        <input
          aria-label="Alias de courrier"
          value={alias}
          onChange={(event) => {
            setAlias(event.target.value)
            setMessage(null)
          }}
          placeholder="alias de courrier"
          className="rounded-field border border-border bg-card px-4 outline-none"
          style={{ height: 48, fontSize: 16, width: 180 }}
        />
        <button
          type="button"
          disabled={nom.trim().length === 0 || !modifie || enregistrer.isPending}
          onClick={() => enregistrer.mutate()}
          className="rounded-button bg-accent px-4 text-card-title font-semibold text-card disabled:opacity-40"
          style={{ height: 48 }}
        >
          Enregistrer
        </button>
      </div>

      {adresseComplete ? (
        <p className="mt-2 text-meta text-ink-muted">
          Écrire à <b>{adresseComplete}</b> dépose une fiche dans cet espace. Seuls ses membres
          peuvent le faire.
        </p>
      ) : (
        <p className="mt-2 text-meta text-ink-muted">
          Sans alias, cet espace n'est joignable par aucun message.
        </p>
      )}

      {message ? <p className="mt-2 text-meta text-ink-muted">{message}</p> : null}
    </div>
  )
}

function Utilisateurs({ moi }: { moi: Me }) {
  const { data, isPending } = useQuery<readonly AdminUser[]>({
    queryKey: ['admin', 'utilisateurs'],
    queryFn: api.adminUsers,
  })

  if (isPending) {
    return <Chargement />
  }

  return (
    <>
      <CreerCompte />

      <ul className="flex flex-col gap-3">
        {data?.map((utilisateur) => (
          <LigneUtilisateur key={utilisateur.id} utilisateur={utilisateur} moi={moi} />
        ))}
      </ul>
    </>
  )
}

/**
 * Crée un compte. Il n'a pas de mot de passe : la personne entrera par lien magique, comme tout
 * compte ordinaire. L'administrateur ouvre la porte, il ne choisit pas la clé.
 */
function CreerCompte() {
  const [email, setEmail] = useState('')
  const [prenom, setPrenom] = useState('')
  const [nomFamille, setNomFamille] = useState('')
  const [motDePasse, setMotDePasse] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [engendre, setEngendre] = useState<CreatedUser | null>(null)
  const queryClient = useQueryClient()

  const creer = useMutation({
    mutationFn: () =>
      api.adminCreateUser({
        email: email.trim(),
        ...(prenom.trim() ? { firstName: prenom.trim() } : {}),
        ...(nomFamille.trim() ? { lastName: nomFamille.trim() } : {}),
        ...(motDePasse ? { password: motDePasse } : {}),
      }),
    onSuccess: async (cree: CreatedUser) => {
      // Le mot de passe engendré n'est rendu qu'ici : il n'est stocké que haché, et cet écran
      // est la seule occasion de le lire si le courrier n'arrive pas.
      setEngendre(cree)
      setMessage(null)
      setEmail('')
      setPrenom('')
      setNomFamille('')
      setMotDePasse('')
      await queryClient.invalidateQueries({ queryKey: ['admin'] })
    },
    onError: (cause: unknown) =>
      setMessage(cause instanceof ApiError ? cause.message : 'La création a échoué.'),
  })

  return (
    <section className="mb-6 rounded-card border border-border bg-card p-4">
      <h2 className="font-serif text-record">Créer un compte</h2>

      <div className="mt-3 flex flex-wrap gap-3">
        <input
          type="email"
          inputMode="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value)
            setMessage(null)
          }}
          placeholder="adresse@exemple.fr"
          className="grow rounded-field border border-border bg-card px-4 outline-none"
          style={{ height: 48, fontSize: 16, minWidth: 200 }}
        />
        <input
          value={prenom}
          onChange={(event) => setPrenom(event.target.value)}
          placeholder="Prénom"
          className="grow rounded-field border border-border bg-card px-4 outline-none"
          style={{ height: 48, fontSize: 16, minWidth: 140 }}
        />
        <input
          value={nomFamille}
          onChange={(event) => setNomFamille(event.target.value)}
          placeholder="Nom"
          className="grow rounded-field border border-border bg-card px-4 outline-none"
          style={{ height: 48, fontSize: 16, minWidth: 140 }}
        />
        {/* En clair : on le transmet à la personne, il doit être lisible. */}
        <input
          type="text"
          autoComplete="off"
          value={motDePasse}
          onChange={(event) => {
            setMotDePasse(event.target.value)
            setMessage(null)
          }}
          placeholder="Mot de passe (engendré si vide)"
          className="grow rounded-field border border-border bg-card px-4 outline-none"
          style={{ height: 48, fontSize: 16, minWidth: 200 }}
        />
        <button
          type="button"
          disabled={
            email.trim().length === 0 ||
            (motDePasse.length > 0 && motDePasse.length < 10) ||
            creer.isPending
          }
          onClick={() => creer.mutate()}
          className="rounded-button bg-accent px-4 text-card-title font-semibold text-card disabled:opacity-40"
          style={{ height: 48 }}
        >
          Créer
        </button>
      </div>

      <p className="mt-2 text-meta text-ink-muted">
        Sans mot de passe, un mot de passe lisible est engendré et envoyé par courrier. Dans tous
        les cas, il devra le changer à sa première connexion.
      </p>

      {message ? <p className="mt-2 text-meta text-danger">{message}</p> : null}

      {/* Affiché jusqu'à ce que l'administrateur ferme : ce mot de passe n'est stocké que haché,
          c'est la seule occasion de le lire si le courrier n'arrive pas. */}
      {engendre ? (
        <div className="mt-4 rounded-card bg-surface p-4">
          <p className="text-card-title font-semibold">
            Compte créé pour {engendre.user.displayName}
          </p>

          {engendre.generatedPassword ? (
            <>
              <p className="mt-2 text-meta text-ink-muted">
                Mot de passe engendré, à noter maintenant :
              </p>
              <p className="mt-1 select-all rounded-field bg-card p-3 text-card-title font-semibold">
                {engendre.generatedPassword}
              </p>
              <p className="mt-2 text-meta text-ink-muted">
                Il ne sera plus jamais affiché : seule son empreinte est conservée.
              </p>
            </>
          ) : (
            <p className="mt-2 text-meta text-ink-muted">
              Il se connecte avec le mot de passe que vous avez saisi.
            </p>
          )}

          {engendre.mailNotice ? (
            <p className="mt-3 text-meta text-danger">{engendre.mailNotice}</p>
          ) : (
            <p className="mt-3 text-meta text-accent">
              Un courrier lui a été envoyé avec ses identifiants.
            </p>
          )}

          <button
            type="button"
            onClick={() => setEngendre(null)}
            className="mt-3 text-meta font-semibold text-accent"
          >
            J'ai noté, fermer
          </button>
        </div>
      ) : null}
    </section>
  )
}

/**
 * Espaces d'un compte, vus et gérés depuis sa fiche.
 *
 * Les mêmes opérations existent depuis la fiche de l'espace ; c'est le même endpoint des deux
 * côtés. Ce qui change est le point de départ : « à quels espaces cette personne appartient »
 * plutôt que « qui appartient à cet espace ».
 */
function EspacesDuCompte({ utilisateur }: { utilisateur: AdminUser }) {
  const [choisi, setChoisi] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data: tous } = useQuery<readonly AdminSpace[]>({
    queryKey: ['admin', 'espaces'],
    queryFn: api.adminSpaces,
  })

  const rafraichir = () => queryClient.invalidateQueries({ queryKey: ['admin'] })

  const echoue = (cause: unknown) =>
    setMessage(cause instanceof ApiError ? cause.message : "L'opération a échoué.")

  const attribuer = useMutation({
    mutationFn: () => api.adminAddMember(choisi, utilisateur.id),
    onSuccess: async () => {
      setChoisi('')
      setMessage(null)
      await rafraichir()
    },
    onError: echoue,
  })

  const retirer = useMutation({
    mutationFn: (spaceId: string) => api.adminRemoveMember(spaceId, utilisateur.id),
    onSuccess: rafraichir,
    onError: echoue,
  })

  const dejaDedans = new Set(utilisateur.spaces.map((espace) => espace.id))
  const candidats = (tous ?? []).filter((espace) => !dejaDedans.has(espace.id))

  return (
    <section className="mt-3 rounded-card bg-surface p-4">
      {utilisateur.spaces.length === 0 ? (
        <p className="text-meta text-ink-muted">Ce compte n'appartient à aucun espace.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {utilisateur.spaces.map((espace) => (
            <li key={espace.id} className="flex flex-wrap items-center gap-3">
              <span
                className="flex items-center justify-center rounded-field font-semibold text-card"
                style={{
                  width: 32,
                  height: 32,
                  fontSize: 13,
                  background: `var(--color-cat-${espace.emblemHue}-mark)`,
                }}
              >
                {espace.emblemInitials}
              </span>
              <span className="grow text-body">
                {espace.name} <span className="text-meta text-ink-muted">({espace.role})</span>
              </span>
              <button
                type="button"
                onClick={() => retirer.mutate(espace.id)}
                className="text-meta font-semibold"
                style={{ color: 'var(--color-danger)' }}
              >
                Retirer
              </button>
            </li>
          ))}
        </ul>
      )}

      {candidats.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-3">
          <select
            value={choisi}
            onChange={(event) => {
              setChoisi(event.target.value)
              setMessage(null)
            }}
            className="grow rounded-field border border-border bg-card px-4 outline-none"
            style={{ height: 48, fontSize: 16, minWidth: 200 }}
          >
            <option value="">Attribuer un espace…</option>
            {candidats.map((espace) => (
              <option key={espace.id} value={espace.id}>
                {espace.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={choisi === '' || attribuer.isPending}
            onClick={() => attribuer.mutate()}
            className="rounded-button bg-accent px-4 text-card-title font-semibold text-card disabled:opacity-40"
            style={{ height: 48 }}
          >
            Attribuer
          </button>
        </div>
      ) : (
        <p className="mt-4 text-meta text-ink-muted">
          Ce compte appartient déjà à tous les espaces existants.
        </p>
      )}

      {message ? (
        <p className="mt-3 text-meta" style={{ color: 'var(--color-danger)' }}>
          {message}
        </p>
      ) : null}
    </section>
  )
}

/** Rattache un compte existant à l'espace, sans passer par un code d'invitation. */
function AjouterMembre({
  spaceId,
  membres,
}: {
  spaceId: string
  membres: readonly { userId: string }[]
}) {
  const [choisi, setChoisi] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data: utilisateurs } = useQuery<readonly AdminUser[]>({
    queryKey: ['admin', 'utilisateurs'],
    queryFn: api.adminUsers,
  })

  const ajouter = useMutation({
    mutationFn: () => api.adminAddMember(spaceId, choisi),
    onSuccess: async () => {
      setChoisi('')
      setMessage(null)
      await queryClient.invalidateQueries({ queryKey: ['admin'] })
    },
    onError: (cause: unknown) =>
      setMessage(cause instanceof ApiError ? cause.message : "L'ajout a échoué."),
  })

  const dejaMembres = new Set(membres.map((membre) => membre.userId))
  const candidats = (utilisateurs ?? []).filter((utilisateur) => !dejaMembres.has(utilisateur.id))

  if (candidats.length === 0) {
    return null
  }

  return (
    <div className="mt-4 flex flex-wrap gap-3">
      <select
        value={choisi}
        onChange={(event) => setChoisi(event.target.value)}
        className="grow rounded-field border border-border bg-card px-4 outline-none"
        style={{ height: 48, fontSize: 16, minWidth: 200 }}
      >
        <option value="">Ajouter un membre…</option>
        {candidats.map((utilisateur) => (
          <option key={utilisateur.id} value={utilisateur.id}>
            {utilisateur.displayName} · {utilisateur.email}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={choisi === '' || ajouter.isPending}
        onClick={() => ajouter.mutate()}
        className="rounded-button bg-accent px-4 text-card-title font-semibold text-card disabled:opacity-40"
        style={{ height: 48 }}
      >
        Ajouter
      </button>
      {message ? <span className="text-meta" style={{ color: 'var(--color-danger)' }}>{message}</span> : null}
    </div>
  )
}

function LigneUtilisateur({ utilisateur, moi }: { utilisateur: AdminUser; moi: Me }) {
  const queryClient = useQueryClient()
  const [confirmation, setConfirmation] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [supprimerOuvert, setSupprimerOuvert] = useState(false)
  const [motDePasseOuvert, setMotDePasseOuvert] = useState(false)
  const [motDePasse, setMotDePasse] = useState('')
  const [espacesOuvert, setEspacesOuvert] = useState(false)
  const [modifierOuvert, setModifierOuvert] = useState(false)
  const [nom, setNom] = useState(utilisateur.displayName)
  const [adresse, setAdresse] = useState(utilisateur.email)

  const rafraichir = () => queryClient.invalidateQueries({ queryKey: ['admin'] })

  const modifier = useMutation({
    mutationFn: () =>
      api.adminUpdateUser(utilisateur.id, {
        ...(nom.trim() !== utilisateur.displayName ? { displayName: nom.trim() } : {}),
        ...(adresse.trim() !== utilisateur.email ? { email: adresse.trim() } : {}),
      }),
    onSuccess: async () => {
      setModifierOuvert(false)
      setMessage('Compte modifié.')
      await rafraichir()
    },
    onError: (cause: unknown) =>
      setMessage(cause instanceof ApiError ? cause.message : 'La modification a échoué.'),
  })

  const basculer = useMutation({
    mutationFn: () => api.adminSetAdmin(utilisateur.id, !utilisateur.isAdmin),
    onSuccess: rafraichir,
    onError: (cause: unknown) =>
      setMessage(cause instanceof ApiError ? cause.message : 'Le changement a échoué.'),
  })

  const poser = useMutation({
    mutationFn: () => api.adminSetPassword(utilisateur.id, motDePasse),
    onSuccess: async () => {
      setMotDePasse('')
      setMotDePasseOuvert(false)
      setMessage('Mot de passe posé. Ses sessions ont été fermées.')
      await rafraichir()
    },
    onError: (cause: unknown) =>
      setMessage(cause instanceof ApiError ? cause.message : "L'opération a échoué."),
  })

  const supprimer = useMutation({
    mutationFn: () => api.adminDeleteUser(utilisateur.id, confirmation),
    onSuccess: rafraichir,
    onError: (cause: unknown) =>
      setMessage(cause instanceof ApiError ? cause.message : 'La suppression a échoué.'),
  })

  const soiMeme = utilisateur.id === moi.id

  return (
    <li className="rounded-card border border-border bg-card p-4">
      <p className="text-card-title font-semibold">
        {utilisateur.displayName}
        {utilisateur.isAdmin ? (
          <span className="ml-2 rounded-full bg-accent-soft px-3 text-meta text-accent">
            administrateur
          </span>
        ) : null}
        {soiMeme ? <span className="ml-2 text-meta text-ink-muted">(vous)</span> : null}
      </p>
      <p className="mt-1 text-meta text-ink-muted">
        {utilisateur.email}
        {utilisateur.username ? ` · identifiant ${utilisateur.username}` : ''} ·{' '}
        {utilisateur.spaceCount} espace{utilisateur.spaceCount > 1 ? 's' : ''} ·{' '}
        {utilisateur.hasPassword ? 'mot de passe défini' : 'lien e-mail seulement'}
      </p>
      <p className="mt-1 text-meta text-ink-muted">
        Inscrit le {new Date(utilisateur.createdAt).toLocaleDateString('fr-FR')}
        {utilisateur.lastSeenAt
          ? ` · vu le ${new Date(utilisateur.lastSeenAt).toLocaleDateString('fr-FR')}`
          : ' · jamais connecté'}
      </p>

      <div className="mt-3 flex flex-wrap gap-4">
        <button
          type="button"
          disabled={soiMeme && utilisateur.isAdmin}
          onClick={() => basculer.mutate()}
          className="text-meta font-semibold text-accent disabled:text-ink-muted"
        >
          {utilisateur.isAdmin ? 'Retirer les droits' : 'Rendre administrateur'}
        </button>

        <button
          type="button"
          onClick={() => {
            setModifierOuvert((ouvert) => !ouvert)
            setNom(utilisateur.displayName)
            setAdresse(utilisateur.email)
            setMessage(null)
          }}
          className="text-meta font-semibold text-accent"
        >
          {modifierOuvert ? 'Annuler' : 'Modifier'}
        </button>

        <button
          type="button"
          onClick={() => setEspacesOuvert((ouvert) => !ouvert)}
          className="text-meta font-semibold text-accent"
        >
          {espacesOuvert ? 'Masquer les espaces' : 'Gérer ses espaces'}
        </button>

        <button
          type="button"
          onClick={() => setMotDePasseOuvert((ouvert) => !ouvert)}
          className="text-meta font-semibold text-accent"
        >
          {utilisateur.hasPassword ? 'Réinitialiser le mot de passe' : 'Donner un mot de passe'}
        </button>

        {soiMeme ? null : (
          <button
            type="button"
            onClick={() => setSupprimerOuvert((ouvert) => !ouvert)}
            className="text-meta font-semibold"
            style={{ color: 'var(--color-danger)' }}
          >
            Supprimer le compte
          </button>
        )}
      </div>

      {modifierOuvert ? (
        <div className="mt-3 flex flex-col gap-3">
          <input
            autoFocus
            value={nom}
            onChange={(event) => {
              setNom(event.target.value)
              setMessage(null)
            }}
            placeholder="Nom affiché"
            aria-label="Nom affiché"
            className="w-full rounded-field border border-border bg-card px-4 outline-none"
            style={{ height: 48, fontSize: 16 }}
          />
          <input
            type="email"
            autoComplete="off"
            value={adresse}
            onChange={(event) => {
              setAdresse(event.target.value)
              setMessage(null)
            }}
            placeholder="adresse@exemple.fr"
            aria-label="Adresse e-mail"
            className="w-full rounded-field border border-border bg-card px-4 outline-none"
            style={{ height: 48, fontSize: 16 }}
          />

          {/* Dit avant de valider : l'adresse n'est pas une étiquette, c'est l'identifiant de
              connexion et la destination des courriers de l'application. */}
          <p className="text-meta text-ink-muted">
            L'adresse sert à se connecter et reçoit les courriers : la changer change les deux.
            Le mot de passe, lui, ne bouge pas.
          </p>

          <div>
            <button
              type="button"
              disabled={
                modifier.isPending
                || nom.trim().length === 0
                || adresse.trim().length === 0
                || (nom.trim() === utilisateur.displayName && adresse.trim() === utilisateur.email)
              }
              onClick={() => modifier.mutate()}
              className="rounded-button bg-accent px-4 text-card-title font-semibold text-card disabled:opacity-40"
              style={{ height: 48 }}
            >
              {modifier.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      ) : null}

      {espacesOuvert ? <EspacesDuCompte utilisateur={utilisateur} /> : null}

      {motDePasseOuvert ? (
        <div className="mt-3 flex flex-wrap gap-3">
          {/* En clair, et volontairement : c'est un mot de passe qu'on transmet à quelqu'un, il
              doit être lisible pour être dicté. La confirmation n'a pas de sens ici — celui qui
              le pose le voit. */}
          <input
            type="text"
            autoComplete="off"
            value={motDePasse}
            onChange={(event) => {
              setMotDePasse(event.target.value)
              setMessage(null)
            }}
            placeholder="Nouveau mot de passe, 10 caractères minimum"
            className="grow rounded-field border border-border bg-card px-4 outline-none"
            style={{ height: 48, fontSize: 16, minWidth: 240 }}
          />
          <button
            type="button"
            disabled={motDePasse.length < 10 || poser.isPending}
            onClick={() => poser.mutate()}
            className="rounded-button bg-accent px-4 text-card-title font-semibold text-card disabled:opacity-40"
            style={{ height: 48 }}
          >
            Poser
          </button>
        </div>
      ) : null}

      {supprimerOuvert ? (
        <div className="mt-3 flex flex-wrap gap-3">
          <input
            value={confirmation}
            onChange={(event) => {
              setConfirmation(event.target.value)
              setMessage(null)
            }}
            placeholder={utilisateur.email}
            className="grow rounded-field border border-border bg-card px-4 outline-none"
            style={{ height: 48, fontSize: 16, minWidth: 220 }}
          />
          <button
            type="button"
            disabled={confirmation.trim().toLowerCase() !== utilisateur.email.toLowerCase()}
            onClick={() => supprimer.mutate()}
            className="rounded-button px-4 text-card-title font-semibold text-card disabled:opacity-40"
            style={{ height: 48, background: 'var(--color-danger)' }}
          >
            Supprimer
          </button>
        </div>
      ) : null}

      {message ? (
        <p className="mt-2 text-meta" style={{ color: 'var(--color-danger)' }}>
          {message}
        </p>
      ) : null}
    </li>
  )
}

/** Changement de mot de passe. Toutes les sessions du compte sont révoquées au passage. */
function Compte() {
  const [actuel, setActuel] = useState('')
  const [nouveau, setNouveau] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  const changer = useMutation({
    mutationFn: () => api.changePassword(actuel, nouveau),
    onSuccess: () => {
      setMessage('Mot de passe changé. Toutes les sessions ont été fermées, reconnectez-vous.')
      setActuel('')
      setNouveau('')
    },
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : 'Le changement a échoué.'),
  })

  return (
    <section className="rounded-card border border-border bg-card p-4" style={{ maxWidth: 420 }}>
      <h2 className="font-serif text-record">Changer le mot de passe</h2>

      <label className="mt-4 block text-label font-semibold uppercase text-ink-muted" htmlFor="actuel">
        Mot de passe actuel
      </label>
      <input
        id="actuel"
        type="password"
        autoComplete="current-password"
        value={actuel}
        onChange={(event) => {
          setActuel(event.target.value)
          setErreur(null)
        }}
        className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
        style={{ height: 56, fontSize: 17 }}
      />

      <label className="mt-4 block text-label font-semibold uppercase text-ink-muted" htmlFor="nouveau">
        Nouveau mot de passe
      </label>
      <input
        id="nouveau"
        type="password"
        autoComplete="new-password"
        value={nouveau}
        onChange={(event) => {
          setNouveau(event.target.value)
          setErreur(null)
        }}
        className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
        style={{ height: 56, fontSize: 17 }}
      />
      <p className="mt-2 text-meta text-ink-muted">Au moins 10 caractères.</p>

      {erreur ? (
        <p className="mt-3 text-meta" style={{ color: 'var(--color-danger)' }}>
          {erreur}
        </p>
      ) : null}
      {message ? <p className="mt-3 text-meta text-accent">{message}</p> : null}

      <button
        type="button"
        disabled={actuel.length === 0 || nouveau.length < 10 || changer.isPending}
        onClick={() => changer.mutate()}
        className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
        style={{ height: 56 }}
      >
        Changer
      </button>
    </section>
  )
}

function Cadre({ children }: { children: ReactNode }) {
  return (
    <main
      className="mx-auto min-h-dvh bg-surface"
      style={{ maxWidth: 900, paddingInline: layout.gutter, paddingBottom: 48 }}
    >
      {children}
    </main>
  )
}

function Chargement() {
  return (
    <ul className="flex flex-col gap-3">
      {[0, 1, 2].map((index) => (
        <li key={index} className="rounded-card border border-border bg-card p-4">
          <div className="rounded-field bg-sunken" style={{ height: 17 }} />
          <div className="mt-2 rounded-field bg-sunken" style={{ height: 13, width: '50%' }} />
        </li>
      ))}
    </ul>
  )
}
